import { calculateInvoiceSummary } from '@/composables/useInvoiceCalculations';
import { INVOICE_CURRENCY, INVOICE_VAT_RATE } from '@/constants/invoiceDefaults';
import {
	QUOTATION_DOCUMENT_TYPE,
	normalizeQuotationContent,
} from '@/utils/quotationDocument';
import {
	isInvoiceDueOnReceipt,
	normalizeInvoicePaymentTerms,
} from '@/utils/invoicePaymentTerms';

const ZERO_VAT_RATE = 0;

const localTodayIso = () => {
	const today = new Date();
	const year = today.getFullYear();
	const month = String(today.getMonth() + 1).padStart(2, '0');
	const day = String(today.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

const normalizeId = (value) => {
	if (value === null || value === undefined || value === '') return null;
	return value;
};

const normalizeIsoDate = (value) => {
	const match = String(value || '').match(/^(\d{4}-\d{2}-\d{2})/);
	return match?.[1] || '';
};

const normalizeLookupText = (value) =>
	String(value || '')
		.trim()
		.replace(/\s+/g, ' ')
		.toLocaleLowerCase('en-GB');

const idsEqual = (left, right) =>
	left !== null &&
	left !== undefined &&
	right !== null &&
	right !== undefined &&
	String(left) === String(right);

const optionId = (option) => normalizeId(option?.id ?? option?.value);

const findOptionById = (options = [], id) =>
	normalizeId(id) === null
		? null
		: (options || []).find(
				(option) =>
					idsEqual(optionId(option), id) || idsEqual(option?.documentId, id),
			) || null;

const optionName = (option) =>
	String(
		option?.catalogDisplayName ||
			option?.name ||
			option?.label ||
			option?.title ||
			'',
	).trim();

const optionText = (option, keys = []) => {
	for (const key of keys) {
		const value = option?.[key] ?? option?.attributes?.[key];
		if (value === null || value === undefined) continue;
		const normalized = String(value).trim();
		if (normalized) return normalized;
	}
	return '';
};

const resolveClient = (quotation, content, clientOptions = []) => {
	const explicitId =
		normalizeId(content.clientId) ??
		normalizeId(quotation?.client?.id) ??
		normalizeId(quotation?.clientId);
	if (explicitId !== null) {
		const explicitOption = findOptionById(clientOptions, explicitId);
		if (explicitOption || !clientOptions.length) {
			return {
				id: explicitOption ? optionId(explicitOption) : explicitId,
				option: explicitOption,
			};
		}
	}

	const clientName = normalizeLookupText(content.clientName || quotation?.clientName);
	if (!clientName) return { id: null, option: null };
	const matches = (clientOptions || []).filter(
		(option) => normalizeLookupText(optionName(option)) === clientName,
	);
	return matches.length === 1
		? { id: optionId(matches[0]), option: matches[0] }
		: { id: null, option: null };
};

const resolveService = (content, serviceOptions = []) => {
	const explicitId = normalizeId(content.serviceDetails?.serviceId);
	const serviceText = String(
		content.serviceDetails?.serviceType || content.subtitle || '',
	).trim();
	if (explicitId !== null) {
		const explicitOption = findOptionById(serviceOptions, explicitId);
		if (explicitOption || !serviceOptions.length) {
			return {
				id: explicitId,
				name: serviceText || optionName(explicitOption),
				needsReview: false,
			};
		}
	}

	const normalizedServiceText = normalizeLookupText(serviceText);
	if (!normalizedServiceText) {
		return {
			id: null,
			name: '',
			needsReview: explicitId !== null && serviceOptions.length > 0,
		};
	}
	const matches = (serviceOptions || []).filter(
		(option) => normalizeLookupText(optionName(option)) === normalizedServiceText,
	);
	return matches.length === 1
		? {
				id: optionId(matches[0]),
				name: serviceText || optionName(matches[0]),
				needsReview: false,
			}
		: {
				id: null,
				name: serviceText,
				needsReview: serviceOptions.length > 0,
			};
};

const relationIds = (value) => {
	const entries = Array.isArray(value?.data)
		? value.data
		: Array.isArray(value)
			? value
			: [];
	return entries
		.map((entry) => normalizeId(entry?.id ?? entry?.value ?? entry))
		.filter((id) => id !== null);
};

const buildingAddress = (building) =>
	String(
		building?.address ||
			building?.Address ||
			building?.siteAddress ||
			building?.data?.address ||
			'',
	).trim();

const resolveBuilding = (content, clientOption, buildingOptions = []) => {
	const linkedIds = relationIds(clientOption?.buildings);
	const hasLinkedBuildingData =
		Array.isArray(clientOption?.buildings) || Array.isArray(clientOption?.buildings?.data);
	const linkedOptions = hasLinkedBuildingData
		? (buildingOptions || []).filter((building) =>
				linkedIds.some((id) => idsEqual(optionId(building), id)),
			)
		: buildingOptions || [];
	const explicitId = normalizeId(content.serviceDetails?.propertyId);
	if (explicitId !== null) {
		const explicitBuilding = findOptionById(linkedOptions, explicitId);
		if (explicitBuilding) {
			return {
				id: optionId(explicitBuilding),
				name: optionName(explicitBuilding),
			};
		}
	}

	const siteAddress = normalizeLookupText(content.serviceDetails?.siteAddress);
	const propertyName = normalizeLookupText(content.serviceDetails?.property);
	const exactMatches = linkedOptions.filter((building) => {
		const nameMatches =
			Boolean(propertyName) && normalizeLookupText(optionName(building)) === propertyName;
		const addressMatches =
			Boolean(siteAddress) && normalizeLookupText(buildingAddress(building)) === siteAddress;
		return nameMatches || addressMatches;
	});
	if (exactMatches.length === 1) {
		return {
			id: optionId(exactMatches[0]),
			name: optionName(exactMatches[0]),
		};
	}
	if (hasLinkedBuildingData && linkedOptions.length === 1) {
		return {
			id: optionId(linkedOptions[0]),
			name: optionName(linkedOptions[0]),
		};
	}
	return { id: null, name: '' };
};

const resolveServiceRange = (content, invoiceDate) => {
	const phaseDates = (content.schedule?.phases || [])
		.map((phase) => normalizeIsoDate(phase?.date))
		.filter(Boolean)
		.sort();
	const fallbackDate = invoiceDate;
	return {
		dateFrom: phaseDates[0] || fallbackDate,
		dateTo: phaseDates.at(-1) || fallbackDate,
		hasScheduledDates: phaseDates.length > 0,
	};
};

const resolveInvoiceVatRate = (quotationVatRate) =>
	Number(quotationVatRate) === ZERO_VAT_RATE ? ZERO_VAT_RATE : INVOICE_VAT_RATE;

const buildInvoiceLines = (
	content,
	{ buildingId = null, buildingName = '', serviceId = null, serviceName = '' } = {},
) => {
	const fallbackServiceName = String(
		serviceName || content.serviceDetails?.serviceType || content.subtitle || '',
	).trim();
	const pricedRows = (content.costBreakdown?.rows || []).filter((row) => {
		if (row?.amount === null || row?.amount === undefined || row?.amount === '') return false;
		return Number.isFinite(Number(row.amount));
	});

	if (!pricedRows.length) {
		return [
			{
				description: fallbackServiceName || 'Quoted service',
				serviceId,
				serviceName: fallbackServiceName || 'Quoted service',
				flatRef: '',
				hours: '',
				rate: '',
				amountMode: 'direct',
				directAmount: '',
				amount: '',
				manual: true,
				buildingId,
				buildingName,
			},
		];
	}

	return pricedRows.map((row, index) => {
		const description = String(row.item || fallbackServiceName || 'Quoted service').trim();
		const isPrimaryServiceLine = index === 0;
		const amount = Number(row.amount);
		return {
			description,
			serviceId: isPrimaryServiceLine ? serviceId : null,
			serviceName: isPrimaryServiceLine ? fallbackServiceName || description : description,
			flatRef: '',
			hours: '',
			rate: '',
			amountMode: 'direct',
			directAmount: amount,
			amount,
			manual: true,
			buildingId,
			buildingName,
		};
	});
};

/**
 * Creates an unsaved client-invoice snapshot from an existing quotation.
 *
 * The quotation stays untouched. Cost rows are net invoice lines; the quotation's
 * gross total is deliberately not copied as a line because invoice totals add VAT.
 */
export const buildInvoiceDraftFromQuotation = (
	quotation = {},
	{
		invoiceDate = localTodayIso(),
		clientOptions = [],
		buildingOptions = [],
		serviceOptions = [],
	} = {},
) => {
	const documentType = quotation?.documentType || quotation?.content?.documentType;
	if (documentType && documentType !== QUOTATION_DOCUMENT_TYPE) {
		throw new Error('Only quotation documents can be converted to client invoices.');
	}

	const content = normalizeQuotationContent(quotation?.content || quotation);
	const normalizedInvoiceDate = normalizeIsoDate(invoiceDate) || localTodayIso();
	const serviceRange = resolveServiceRange(content, normalizedInvoiceDate);
	const client = resolveClient(quotation, content, clientOptions);
	const service = resolveService(content, serviceOptions);
	const building = resolveBuilding(content, client.option, buildingOptions);
	const quotationDocumentId =
		normalizeId(quotation?.documentId) ?? normalizeId(quotation?.id);
	const quotationReference =
		String(content.quotationNumber || quotation?.reference || '').trim();
	const buildingName =
		building.name ||
		String(content.serviceDetails?.siteAddress || content.serviceDetails?.property || '').trim();
	const items = buildInvoiceLines(content, {
		buildingId: building.id,
		buildingName,
		serviceId: service.id,
		serviceName: service.name,
	});
	const vatRate = resolveInvoiceVatRate(content.costBreakdown?.vatRate);
	const invoiceContent = {
		currency: INVOICE_CURRENCY,
		vatRate,
		buildingScope: building.id === null ? 'all' : 'single',
		dateRanges: [
			{
				dateFrom: serviceRange.dateFrom,
				dateTo: serviceRange.dateTo,
				sources: {
					appointments: { enabled: false, all: false, serviceGroupIds: [] },
					purchases: { enabled: false, all: false, cosIds: [] },
				},
				sourceType: 'custom',
				serviceGroupIds: [],
				serviceGroupId: null,
				cosId: null,
				excludedPurchaseIds: [],
				excludedAppointmentIds: [],
				includedPurchaseIds: [],
				items,
			},
		],
		customDates: [],
		summary: {
			subtotal: 0,
			vat: 0,
			total: 0,
		},
	};
	invoiceContent.summary = calculateInvoiceSummary(invoiceContent, vatRate);

	const warnings = [];
	const quotationVatRate = Number(content.costBreakdown?.vatRate);
	if (
		Number.isFinite(quotationVatRate) &&
		quotationVatRate !== ZERO_VAT_RATE &&
		quotationVatRate !== INVOICE_VAT_RATE
	) {
		warnings.push(
			`The quotation uses ${quotationVatRate * 100}% VAT, but client invoices support only 0% or 20%. Review VAT before saving.`,
		);
	}
	if (content.currency && content.currency !== INVOICE_CURRENCY) {
		warnings.push(
			`The quotation currency is ${content.currency}; client invoices are saved in ${INVOICE_CURRENCY}.`,
		);
	}
	if (client.id === null) {
		warnings.push('The quotation client could not be linked. Select a client before saving.');
	}
	if (service.needsReview) {
		warnings.push(
			'The quotation service could not be linked safely. Confirm the service before saving.',
		);
	}
	if (!serviceRange.hasScheduledDates) {
		warnings.push(
			'The quotation has no scheduled work date. The invoice date was used for the service range; confirm it before saving.',
		);
	}
	const hasPricedRows = items.some(
		(item) => item.directAmount !== null && item.directAmount !== undefined && item.directAmount !== '',
	);
	const unpricedRowCount = (content.costBreakdown?.rows || []).filter(
		(row) =>
			row?.amount === null ||
			row?.amount === undefined ||
			row?.amount === '' ||
			!Number.isFinite(Number(row.amount)),
	).length;
	if (!hasPricedRows) {
		warnings.push('The quotation has no priced cost rows. Add an amount before saving.');
	} else {
		if (invoiceContent.summary.subtotal <= 0) {
			warnings.push('The converted invoice subtotal is not positive. Review amounts before saving.');
		}
		if (unpricedRowCount) {
			warnings.push(
				`${unpricedRowCount} unpriced quotation row${unpricedRowCount === 1 ? ' was' : 's were'} not copied to the invoice.`,
			);
		}
	}
	if (
		building.id === null &&
		buildingOptions.length &&
		(content.serviceDetails?.propertyId ||
			content.serviceDetails?.property ||
			content.serviceDetails?.siteAddress)
	) {
		warnings.push('The quotation building could not be linked safely. Confirm the building before saving.');
	}

	const clientTerms = normalizeInvoicePaymentTerms(
		optionText(client.option, ['terms', 'Terms']),
	);

	return {
		invoice: {
			invoiceNumber: '',
			invoiceStatus: 'unpaid',
			invoiceDate: normalizedInvoiceDate,
			dueDate: isInvoiceDueOnReceipt(clientTerms) ? normalizedInvoiceDate : null,
			purchaseOrder: '',
			paidDate: null,
			totalAmount: invoiceContent.summary.total,
			notes: '',
			client: client.id === null ? null : { id: client.id },
			building: building.id === null ? null : { id: building.id },
			clientSnapshot: {
				name: content.clientName || quotation?.clientName || quotation?.client?.name || '',
				buildingName,
				address: content.clientAddress || '',
				customerId: optionText(client.option, ['customerId', 'customer_id', 'Customer ID']),
				terms: clientTerms,
				email: optionText(client.option, ['email', 'Email']),
				telephone: optionText(client.option, [
					'telephone',
					'phone',
					'phoneNumber',
					'Telephone',
					'Phone',
				]),
			},
			invoiceContent,
			metadata: {
				snapshotFrozen: false,
				documentType: 'invoice',
				sourceQuotation: {
					documentId: quotationDocumentId,
					quotationNumber: quotationReference || null,
					reference: content.reference || null,
					quotationDate: normalizeIsoDate(content.quotationDate) || null,
					serviceDateFrom: serviceRange.dateFrom || null,
					serviceDateTo: serviceRange.dateTo || null,
				},
			},
		},
		warnings,
	};
};
